'use client'

import { useEffect, useRef, useState } from 'react'
import GradientText from './GradientText'

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)
const easeInOut = (t: number) => {
  const clamped = clamp(t, 0, 1)
  return clamped * clamped * (3 - 2 * clamped)
}

export default function WhoItsFor() {
  const cards = [
    {
      title: "For the Founder",
      description: "Validate your idea against a realistic market model. Understand your true competitors and find the perfect customer niche before you write a single line of code."
    },
    {
      title: "For the Marketer", 
      description: "De-risk your campaign budget. Simulate the performance of different ad strategies, channels, and creatives to find the optimal plan for maximum ROI."
    },
    {
      title: "For the Product Leader",
      description: "Launching a new feature or entering a new market? Model the GTM strategy, forecast adoption rates, and build a data-driven business case for your next big move."
    }
  ]

  const sectionRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [headingHeight, setHeadingHeight] = useState(0)

  useEffect(() => {
    const resize = () => {
      setViewportHeight(window.innerHeight)
      setIsMobile(window.innerWidth < 768) // 768px is the 'md' breakpoint
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  useEffect(() => {
    const measureHeading = () => {
      if (!headingRef.current) return
      setHeadingHeight(headingRef.current.offsetHeight)
    }

    measureHeading()
    window.addEventListener('resize', measureHeading)
    return () => window.removeEventListener('resize', measureHeading)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updatePreference = () => setPrefersReducedMotion(media.matches)
    updatePreference()

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', updatePreference)
      return () => media.removeEventListener('change', updatePreference)
    }

    // Fallback for older browsers that still use addListener/removeListener
    media.addListener(updatePreference)
    return () => media.removeListener(updatePreference)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion || isMobile) {
      setScrollProgress(1)
      return
    }

    const handleScroll = () => {
      if (!sectionRef.current || viewportHeight === 0) return
      const sectionTop = sectionRef.current.offsetTop
      const animationDistance = Math.max(viewportHeight * 0.9, 1)
      const rawProgress = (window.scrollY - sectionTop) / animationDistance
      setScrollProgress(clamp(rawProgress, 0, 1))
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [prefersReducedMotion, isMobile, viewportHeight])

  const stickyViewportOffset = 64
  const headingHalfHeight = headingHeight ? headingHeight / 2 : 48
  const travelDistance = Math.max(viewportHeight / 2 - (stickyViewportOffset + headingHalfHeight), 0)
  const initialScale = 1.85
  const scalePhaseEnd = 0.45
  const translationPhaseStart = scalePhaseEnd
  const translationPhaseDuration = 0.2
  const translationPhaseEnd = Math.min(translationPhaseStart + translationPhaseDuration, 0.85)
  const cardsRevealStart = translationPhaseEnd
  const cardsRevealEnd = Math.min(cardsRevealStart + 0.25, 0.98)
  const headingAppearStart = 0.08
  const headingAppearEnd = Math.min(scalePhaseEnd, 0.28)

  const disableAnimations = prefersReducedMotion || isMobile

  const shrinkProgress = disableAnimations ? 1 : clamp(scrollProgress / scalePhaseEnd, 0, 1)
  const shrinkEase = disableAnimations ? 1 : easeInOut(shrinkProgress)
  const scale = disableAnimations ? 1 : initialScale - (initialScale - 1) * shrinkEase

  const translationRaw = disableAnimations
    ? 1
    : (scrollProgress - translationPhaseStart) /
        Math.max(translationPhaseEnd - translationPhaseStart, 0.0001)
  const translationEase = disableAnimations ? 1 : easeInOut(translationRaw)
  const stackTranslateY = disableAnimations ? 0 : travelDistance * (1 - translationEase)

  const cardsRaw = disableAnimations
    ? 1
    : (scrollProgress - cardsRevealStart) /
        Math.max(cardsRevealEnd - cardsRevealStart, 0.0001)
  const cardsEase = disableAnimations ? 1 : easeInOut(cardsRaw)

  const baseDelay = disableAnimations ? 0 : clamp((cardsRevealStart - scrollProgress) * -0.12, 0, 0.24)
  const perCardDelay = disableAnimations
    ? () => 0
    : (index: number) => baseDelay + index * 0.12

  const headingOpacity = disableAnimations
    ? 1
    : clamp(
        (scrollProgress - headingAppearStart) /
          Math.max(headingAppearEnd - headingAppearStart, 0.0001),
        0,
        1,
      )
  const headingStyle = disableAnimations
    ? { transform: 'none', transformOrigin: 'center', transition: 'none', opacity: 1 }
    : {
        transform: `scale(${scale})`,
        transformOrigin: 'center',
        opacity: headingOpacity,
        transition: 'transform 120ms ease-out, opacity 200ms ease-out',
      }
  const stickyShellClass = disableAnimations ? '' : 'sticky who-its-for-sticky'
  const stickyShellStyle = disableAnimations
    ? { paddingTop: '2rem', paddingBottom: '2rem' }
    : { top: `${stickyViewportOffset}px` }
  const innerStackClass = disableAnimations
    ? 'flex flex-col items-center text-center'
    : 'flex h-screen flex-col items-center justify-start text-center who-its-for-stack'
  const innerStackStyle = disableAnimations
    ? undefined
    : { transform: `translateY(${stackTranslateY}px)`, transition: 'transform 120ms ease-out' }
  const cardsContainerStyle = disableAnimations
    ? { marginTop: '32px', opacity: 1 }
    : {
        marginTop: '64px',
        opacity: cardsEase,
        transform: `translateY(${(1 - cardsEase) * 64}px)`,
        transition: 'opacity 240ms ease-out, transform 260ms ease-out',
      }

  const containerMinHeightClass = isMobile 
    ? 'relative' 
    : 'relative min-h-auto md:min-h-[150vh] lg:min-h-[200vh] xl:min-h-[240vh] 2xl:min-h-[260vh] who-its-for-container'

  return (
    <section
      ref={sectionRef}
      className="section-spacing relative"
      style={{ backgroundColor: '#000914' }}
    >
      <div className="container-stripe">
        {/* Animated Section Header */}
        <div className="relative">
          <div className={containerMinHeightClass}>
            <div className={`${stickyShellClass} w-full`} style={stickyShellStyle}>
              <div className={innerStackClass} style={innerStackStyle}>
                {/* Scroll anchor for navigation */}
                <div id="who-its-for" style={{ scrollMarginTop: '100px' }}></div>
                <h2
                  ref={headingRef}
                  className="text-3xl sm:text-4xl md:text-6xl font-light text-balance max-w-5xl mx-auto text-white leading-tight drop-shadow-lg tracking-tight px-4 sm:px-0"
                  style={headingStyle}
                >
                  A Strategic Co-Pilot for
                  <span className="block mt-2 sm:mt-3 mb-2 font-normal" style={{ paddingBottom: '8px' }}>
                    <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
                      Every Growth Stage
                    </GradientText>
                  </span>
                </h2>

                <div className="w-full px-4 sm:px-0" style={cardsContainerStyle}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-left">
                    {cards.map((card, index) => (
                      <div
                        key={index}
                        className="group aspect-[3/4] flex flex-col relative overflow-hidden rounded-stripe-lg p-6 sm:p-8 shadow-stripe-sm transition-all duration-300 bg-cover bg-top bg-no-repeat border border-gray-900"
                        style={{ 
                          transitionDelay: disableAnimations ? '0ms' : `${perCardDelay(index)}ms`,
                          ...(index === 0 && { backgroundImage: 'url(/images/founter_monet_1.webp)' }),
                          ...(index === 1 && { backgroundImage: 'url(/images/marketer_monet_1.webp)', backgroundPosition: 'center bottom' }),
                          ...(index === 2 && { backgroundImage: 'url(/images/product_monet_2.webp)' })
                        }}
                      >
                        {/* Dark overlay for all cards */}
                        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/75 to-black/90 rounded-stripe-lg transition-all duration-300"></div>
                        
                        {/* Role Icons - Top Left */}
                        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20">
                          {index === 0 && (
                            // Founder - Lightbulb Icon
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          )}
                          {index === 1 && (
                            // Marketer - Megaphone Icon
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                          )}
                          {index === 2 && (
                            // Product Leader - Cube Icon
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          )}
                        </div>
                        
                        <div className="flex flex-col justify-end h-full relative z-10">
                          <h3 className="text-sm sm:text-base md:text-lg font-normal mb-1 sm:mb-2 text-white leading-tight tracking-tight">
                            {card.title}
                          </h3>
                          <p className="text-sm sm:text-base md:text-lg font-light text-blue-100/95 leading-relaxed drop-shadow-sm">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
